import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';
import FormattedText from '../components/FormattedText';

const getOptimizedImgUrl = (url, width = 800) => {
  if (url && url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  }
  return url;
};

export default function About() {
  const { t, getRouteUrl } = useLanguage();
  const page = t.aboutPage;

  return (
    <main className="bg-[#faf8f5] pt-24 min-h-screen text-stone-900 font-sans">
      <SEO pageKey="about" />
      
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#f7f2ea] border-b border-stone-200/80">
        <div className="grid-container">
          <div className="flex justify-between items-center mb-8 border-b border-stone-300/60 pb-6">
            <span className="text-xs font-bold uppercase tracking-[0.4em] text-[#581c24]">
              {page.badge}
            </span>

            {/* Language Switcher */}
            <LanguageSwitcher />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-bold uppercase tracking-tight text-stone-950 leading-tight">
                {page.title}
              </h1>
              
              <div className="inline-block bg-[#f0e6d8] px-6 py-3 rounded-xl border border-[#d8c2a8]">
                <p className="text-2xl md:text-3xl font-serif italic text-[#581c24] font-medium">
                  {page.quote}
                </p>
              </div>

              <div className="space-y-4 text-base sm:text-lg text-stone-700 leading-relaxed font-normal">
                <p>
                  {page.intro}
                </p>
                <p className="font-medium text-stone-900 italic">
                  {page.introSub}
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border border-stone-200">
                <img 
                  src={getOptimizedImgUrl("https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/fotograf-steen-knarberg-20260827-dsc0561-srgb-2500pix.jpeg", 900)}
                  alt="Eliana Napoli" 
                  className="w-full h-full object-cover" 
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white text-stone-950 p-6 rounded-xl shadow-lg border border-stone-200 hidden md:block">
                <span className="text-3xl font-serif font-bold italic text-[#581c24] block mb-1">
                  {page.yearsBadge}
                </span>
                <p className="text-[10px] font-bold uppercase tracking-widest text-stone-600">
                  {page.yearsText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story & Background Content */}
      <section className="py-16 md:py-24 bg-[#faf8f5]">
        <div className="grid-container max-w-4xl space-y-10 md:space-y-14">
          
          {/* Sommelier & Gastronomi */}
          <div className="bg-white p-8 sm:p-10 md:p-12 rounded-2xl border border-stone-200/90 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-[#581c24]" />
            <div className="space-y-4">
              <p className="text-stone-700 text-base sm:text-lg md:text-xl leading-relaxed font-normal">
                {page.p1}
              </p>
            </div>
          </div>

          {/* Italienske organisationer & Konsortier */}
          <div className="space-y-6">
            <div className="border-b border-stone-200 pb-4">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-serif font-bold text-stone-950 leading-snug">
                {page.p2Intro}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-7 sm:p-8 rounded-2xl border border-stone-200/90 shadow-sm hover:border-[#581c24]/30 transition-colors space-y-3">
                <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#581c24] bg-[#f7f2ea] px-3 py-1 rounded-md border border-[#e8ded0]">
                  {page.baroloLabel}
                </span>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                  <FormattedText text={page.baroloText} />
                </p>
              </div>

              <div className="bg-white p-7 sm:p-8 rounded-2xl border border-stone-200/90 shadow-sm hover:border-[#581c24]/30 transition-colors space-y-3">
                <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#581c24] bg-[#f7f2ea] px-3 py-1 rounded-md border border-[#e8ded0]">
                  {page.valpolicellaLabel}
                </span>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                  <FormattedText text={page.valpolicellaText} />
                </p>
              </div>
            </div>
          </div>

          {/* Wine Ambassador */}
          <div className="bg-[#f7f2ea] p-8 sm:p-10 md:p-12 rounded-2xl border border-[#d8c2a8] shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-[#581c24]" />
            <div className="space-y-3">
              <p className="text-stone-800 text-base sm:text-lg md:text-xl leading-relaxed font-normal">
                <FormattedText text={page.p3Text} />
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Philosophy & Closing Highlight */}
      <section className="py-20 md:py-28 bg-[#581c24] text-[#f7f2ea] relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#802937]/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-black/40 blur-3xl pointer-events-none" />

        <div className="grid-container max-w-4xl text-center space-y-10 relative z-10">
          <div className="space-y-5 max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold tracking-tight text-white leading-snug">
              {page.philosophyTitle}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-[#ebd9c8] font-light leading-relaxed">
              {page.philosophyText}
            </p>
          </div>

          <div className="p-8 sm:p-10 md:p-12 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 text-white font-serif text-lg sm:text-xl md:text-2xl font-bold leading-relaxed shadow-xl max-w-3xl mx-auto">
            "<FormattedText text={page.closingHighlight} />"
          </div>

          <div className="pt-2">
            <Link
              to={getRouteUrl('contact')}
              className="inline-flex items-center gap-3 bg-[#f7f2ea] hover:bg-white text-[#581c24] px-8 py-4 rounded-xl font-bold uppercase tracking-wider text-sm transition-all shadow-lg hover:shadow-2xl group"
            >
              <span>{page.ctaButton}</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Experience Photos Section */}
      <section className="py-20 bg-[#faf8f5]">
        <div className="grid-container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4 aspect-square rounded-2xl overflow-hidden shadow-md border border-stone-200">
              <img 
                src={getOptimizedImgUrl("https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/4b48daac-7874-4af2-bb70-1c8c51ac136e.jpg", 600)} 
                alt="Wine Tasting Details" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
              />
            </div>
            <div className="md:col-span-8 aspect-[16/9] md:aspect-auto rounded-2xl overflow-hidden relative shadow-md border border-stone-200">
              <img 
                src={getOptimizedImgUrl("https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/921469fa-4fa3-4d11-a961-032532a857eb.jpg", 1200)} 
                alt="Wine Pouring Event" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/35 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-2xl font-serif font-bold uppercase tracking-tight mb-1 text-white drop-shadow-md">
                  {page.passionCardTitle}
                </h3>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-stone-200 drop-shadow-sm">
                  {page.passionCardSub}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}
